import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';

// Builds a query string from optional params (removes null/undefined values)
function buildQuery(params) {
  const q = Object.entries(params)
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return q ? `?${q}` : '';
}

export function useEmployees(buId = null) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (myTeam = false) => {
    setLoading(true); setError('');
    try {
      const qs = buildQuery({ myTeam: myTeam || null, bu_id: buId });
      const res = await apiClient.get('/teams/employees' + qs);
      setEmployees(res.data.employees || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load employees');
    } finally { setLoading(false); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/employees', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/employees/${id}`, data); await load(); };
  const remove = async (id) => { await apiClient.delete(`/teams/employees/${id}`); await load(); };

  return { employees, loading, error, reload: load, create, update, remove };
}

export function useHSRecords(buId = null) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = buildQuery({ bu_id: buId });
      const res = await apiClient.get('/teams/hs' + qs);
      setRecords(res.data.records || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load H&S records');
    } finally { setLoading(false); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/hs', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/hs/${id}`, data); await load(); };
  const remove = async (id) => { await apiClient.delete(`/teams/hs/${id}`); await load(); };

  return { records, loading, error, reload: load, create, update, remove };
}

export function usePerformancePlans(buId = null) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = buildQuery({ bu_id: buId });
      const res = await apiClient.get('/teams/performance' + qs);
      setPlans(res.data.plans || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load performance plans');
    } finally { setLoading(false); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/performance', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/performance/${id}`, data); await load(); };
  const remove = async (id) => { await apiClient.delete(`/teams/performance/${id}`); await load(); };

  return { plans, loading, error, reload: load, create, update, remove };
}

export function useYearlyReviews(buId = null) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = buildQuery({ bu_id: buId });
      const res = await apiClient.get('/teams/reviews' + qs);
      setReviews(res.data.reviews || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load reviews');
    } finally { setLoading(false); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/reviews', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/reviews/${id}`, data); await load(); };

  return { reviews, loading, error, reload: load, create, update };
}

export function useOnoItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/teams/ono/items');
      setItems(res.data.items || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/ono/items', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/ono/items/${id}`, data); await load(); };

  return { items, loading, reload: load, create, update };
}

export function useOnoSessions(buId = null) {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ weeklyTotal: 0, ytdTotal: 0, employees: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery({ bu_id: buId });
      const [sessRes, statsRes] = await Promise.all([
        apiClient.get('/teams/ono/sessions' + qs),
        apiClient.get('/teams/ono/stats' + qs),
      ]);
      setSessions(sessRes.data.sessions || []);
      setStats(statsRes.data || { weeklyTotal: 0, ytdTotal: 0, employees: [] });
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/teams/ono/sessions', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/teams/ono/sessions/${id}`, data); await load(); };

  return { sessions, stats, loading, reload: load, create, update };
}

export function useAssignments() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/teams/assignments');
      setAssignments(res.data.assignments || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (employeeId, managerId) => {
    await apiClient.post('/teams/assignments', { employee_id: employeeId, manager_id: managerId });
    await load();
  };
  const unassign = async (employeeId, managerId) => {
    await apiClient.delete(`/teams/assignments/${employeeId}/${managerId}`);
    await load();
  };

  return { assignments, loading, reload: load, assign, unassign };
}
